/** Leitura de planilhas (.xlsx e .csv) para importação, com cabeçalhos em português. */
import { normalize } from './format';

export type Cell = string | number | boolean | Date | null;

export function parseCsv(text: string): string[][] {
  const t = text.replace(/^﻿/, '');
  const first = t.split(/\r?\n/, 1)[0] ?? '';
  const sep = (first.match(/;/g)?.length ?? 0) >= (first.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = []; let row: string[] = []; let cur = ''; let q = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    if (q) { if (c === '"') { if (t[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; continue; }
    if (c === '"') q = true;
    else if (c === sep) { row.push(cur); cur = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && t[i + 1] === '\n') i++; row.push(cur); rows.push(row); row = []; cur = ''; }
    else cur += c;
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.filter(r => r.some(x => x.trim() !== ''));
}

export async function readSheet(file: File): Promise<Cell[][]> {
  if (/\.csv$/i.test(file.name) || file.type === 'text/csv') return parseCsv(await file.text());
  if (/\.xlsx$/i.test(file.name)) { const { default: readXlsxFile } = await import('read-excel-file'); return (await readXlsxFile(file)) as Cell[][]; }
  throw new Error('Formato não suportado. Use .xlsx ou .csv.');
}

const cellText = (c: Cell): string => {
  if (c === null || c === undefined) return '';
  if (c instanceof Date) return `${String(c.getUTCDate()).padStart(2, '0')}/${String(c.getUTCMonth() + 1).padStart(2, '0')}/${c.getUTCFullYear()}`;
  return String(c).trim();
};

/** Converte linhas em objetos a partir de um mapa {campo: [cabeçalhos aceitos]}. Devolve campos obrigatórios ausentes. */
export function mapRows<S extends Record<string, string[]>, K extends keyof S & string = keyof S & string>(rows: Cell[][], spec: S, required: NoInfer<K>[]): { items: Record<K, string | number>[]; missing: string[] } {
  const header = (rows[0] ?? []).map(h => normalize(cellText(h)));
  const idx = {} as Record<K, number>;
  for (const k of Object.keys(spec) as K[]) idx[k] = header.findIndex(h => spec[k]!.some(a => normalize(a) === h));
  const missing = required.filter(k => idx[k] < 0).map(k => spec[k]![0]!);
  const items = rows.slice(1).map(r => {
    const o = {} as Record<K, string | number>;
    for (const k of Object.keys(spec) as K[]) { const c = idx[k] >= 0 ? r[idx[k]] ?? null : null; o[k] = typeof c === 'number' ? c : cellText(c ?? null); }
    return o;
  }).filter(o => Object.values(o).some(x => x !== ''));
  return { items, missing };
}

/** Entrega um arquivo ao usuário. Na página publicada usa a confirmação de download da plataforma; no sistema, um download comum. */
export async function saveBlob(name: string, blob: Blob): Promise<boolean> {
  const host = (window as unknown as { claude?: { use?: (n: string) => Promise<{ save: (r: { filename: string; data: Blob }) => Promise<unknown> } | null> } }).claude;
  if (host?.use) {
    const dl = await host.use('downloads').catch(() => null);
    if (dl) { try { await dl.save({ filename: name, data: blob }); return true; } catch { return false; } }
  }
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  return true;
}

export function downloadCsv(name: string, rows: (string | number | null)[][]) {
  const esc = (v: string | number | null) => { const s = v === null ? '' : String(v); return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  void saveBlob(name, new Blob(['\uFEFF' + rows.map(r => r.map(esc).join(';')).join('\r\n')], { type: 'text/csv;charset=utf-8' }));
}
